/** Event bindings: one activity's export wired to another's trigger (`RUN-008`). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { EventBinding } from "@nebulize/sdk";

import { api, request } from "@/lib/api";

import { exportsOf, triggersOf } from "../../lib/catalog";
import type { AddedActivityRow, CatalogEntry, NamedExport, NamedTrigger } from "../../types";

export const wireKeys = {
  of: (spaceId: string) => ["spaces", spaceId, "event-bindings"] as const,
};

export function useWires(spaceId: string): UseQueryResult<EventBinding[]> {
  return useQuery({
    queryKey: wireKeys.of(spaceId),
    enabled: spaceId !== "",
    queryFn: () => api.get<EventBinding[]>(`/v1/spaces/${spaceId}/event-bindings`),
  });
}

export interface WireDraft {
  source_added_activity_id: string;
  export: string;
  target_added_activity_id: string;
  trigger: string;
  parameters: Record<string, string>;
}

export function useWire(spaceId: string): UseMutationResult<EventBinding, Error, WireDraft> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (draft: WireDraft) =>
      api.post<EventBinding>(`/v1/spaces/${spaceId}/event-bindings`, draft),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: wireKeys.of(spaceId) });
    },
  });
}

export function useUnwire(spaceId: string): UseMutationResult<undefined, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (bindingId: string) =>
      api.delete<undefined>(`/v1/event-bindings/${bindingId}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: wireKeys.of(spaceId) });
    },
  });
}

/** What a rebind may change. Neither end moves, so a mapping is fixed in place, never re-created. */
export interface WireEdit {
  bindingId: string;
  export: string;
  trigger: string;
  parameters: Record<string, string>;
}

export function useRewire(spaceId: string): UseMutationResult<EventBinding, Error, WireEdit> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ bindingId, ...body }: WireEdit) =>
      request<EventBinding>(`/v1/event-bindings/${bindingId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: wireKeys.of(spaceId) });
    },
  });
}

export interface WireSwitch {
  bindingId: string;
  enabled: boolean;
}

export function useSwitchWire(
  spaceId: string,
): UseMutationResult<EventBinding, Error, WireSwitch> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ bindingId, enabled }: WireSwitch) =>
      api.post<EventBinding>(`/v1/event-bindings/${bindingId}/enabled`, { enabled }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: wireKeys.of(spaceId) });
    },
  });
}

/** Every added activity on this space that declares at least one export. */
export function sourcesAmong(
  rows: readonly AddedActivityRow[],
  entryFor: (row: AddedActivityRow) => CatalogEntry | undefined,
): { row: AddedActivityRow; exports: NamedExport[] }[] {
  const out: { row: AddedActivityRow; exports: NamedExport[] }[] = [];
  for (const row of rows) {
    const entry = entryFor(row);
    if (entry === undefined) continue;
    const declared = exportsOf(entry);
    if (declared.length > 0) out.push({ row, exports: declared });
  }
  return out;
}

/** Every added activity on this space that declares at least one trigger. */
export function targetsAmong(
  rows: readonly AddedActivityRow[],
  entryFor: (row: AddedActivityRow) => CatalogEntry | undefined,
): { row: AddedActivityRow; triggers: NamedTrigger[] }[] {
  const out: { row: AddedActivityRow; triggers: NamedTrigger[] }[] = [];
  for (const row of rows) {
    const entry = entryFor(row);
    if (entry === undefined) continue;
    const declared = triggersOf(entry);
    if (declared.length > 0) out.push({ row, triggers: declared });
  }
  return out;
}

/** A required trigger parameter no export field and no explicit mapping fills, by name. */
export function unfilledRequiredParams(
  trigger: NamedTrigger | undefined,
  exportFields: readonly string[],
  mapping: Record<string, string>,
): string[] {
  return (trigger?.params ?? [])
    .filter(
      (param) =>
        param.required && (mapping[param.name] ?? "") === "" && !exportFields.includes(param.name),
    )
    .map((param) => param.name);
}

/** Whether wiring `source` to `target` would close a loop through the wires already on this space. */
export function wouldCycle(
  source: string,
  target: string,
  wires: readonly EventBinding[],
  excludeBindingId?: string,
): boolean {
  if (source === "" || target === "") return false;
  if (source === target) return true;

  const edges = wires
    .filter((one) => one.binding_id !== excludeBindingId)
    .map((one) => ({ source: one.source_added_activity_id, target: one.target_added_activity_id }));

  const stack = [target];
  const seen = new Set<string>();
  while (stack.length > 0) {
    const current = stack.pop();
    if (current === undefined) break;
    if (current === source) return true;
    if (seen.has(current)) continue;
    seen.add(current);
    for (const edge of edges) {
      if (edge.source === current) stack.push(edge.target);
    }
  }
  return false;
}
